// ============================================================
// DOCUMENT INTELLIGENCE — RAG Batch 3
// Handles text extraction from all supported file types
// Called at upload time — extracted text stored with document
// ============================================================

import { getExtractionAuthHeaders } from '../services/documentStorage';

export const documentIntelligence = {

    // Master extraction function — detects file type and routes correctly
    extractText: async (file, onProgress = null) => {
        const ext = file.name.split('.').pop().toLowerCase();
        const report = progress => onProgress && onProgress(progress);

        try {
            report({ stage: 'detecting', message: 'Detecting file type...' });

            if (ext === 'pdf') {
                report({ stage: 'extracting', message: 'Reading PDF pages...' });
                return await documentIntelligence.extractFromPDF(file, onProgress);
            }

            if (['doc', 'docx'].includes(ext)) {
                report({ stage: 'extracting', message: 'Reading Word document...' });
                return await documentIntelligence.extractFromWord(file);
            }

            if (['xls', 'xlsx'].includes(ext)) {
                report({ stage: 'extracting', message: 'Reading spreadsheet...' });
                return await documentIntelligence.extractFromExcel(file);
            }

            if (ext === 'csv') {
                report({ stage: 'extracting', message: 'Reading CSV data...' });
                return await documentIntelligence.extractFromCSV(file);
            }

            if (['txt', 'md'].includes(ext)) {
                report({ stage: 'extracting', message: 'Reading text file...' });
                const text = await file.text();
                return {
                    text,
                    pageCount: 1,
                    wordCount: text.split(/\s+/).filter(Boolean).length,
                    method: 'plain_text',
                    quality: 'full',
                };
            }

            if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic'].includes(ext)) {
                report({ stage: 'image', message: 'Image file detected...' });
                return {
                    text: `[IMAGE_FILE: ${file.name}]`,
                    pageCount: 1,
                    wordCount: 0,
                    method: 'image',
                    quality: 'image_only',
                    note: 'Image files are stored but not AI-searchable in this version.',
                };
            }

            return {
                text: null,
                pageCount: 0,
                wordCount: 0,
                method: 'unsupported',
                quality: 'failed',
                note: `File type .${ext} is not supported for text extraction.`,
            };

        } catch (err) {
            console.error('Text extraction error:', file.name, err);
            return {
                text: null,
                pageCount: 0,
                wordCount: 0,
                method: 'error',
                quality: 'failed',
                error: err.message,
            };
        }
    },

    extractFromPDF: async (file, onProgress = null) => {
        const report = (p) => onProgress && onProgress(p);

        try {
            report({ stage: 'extracting', message: 'Sending to extraction service...', percent: 10 });

            const formData = new FormData();
            formData.append('file', file, file.name);

            const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;

            const response = await fetch(
                `${supabaseUrl}/functions/v1/extract-document-text`,
                {
                    method: 'POST',
                    headers: await getExtractionAuthHeaders(),
                    body: formData,
                }
            );

            if (!response.ok) {
                const err = await response.text();
                throw new Error(`Extraction service error: ${response.status} — ${err}`);
            }

            const result = await response.json();

            report({ stage: 'extracting', message: 'Extraction complete.', percent: 90 });

            if (!result.text || result.wordCount < 20) {
                return {
                    text: '',
                    pageCount: result.pageCount || 0,
                    wordCount: 0,
                    method: result.method || 'pdf_server',
                    quality: 'failed',
                    note: 'No text could be extracted. Document may be a scanned image.',
                };
            }

            return {
                text: result.text,
                pageCount: result.pageCount || 1,
                wordCount: result.wordCount,
                method: result.method || 'pdf_server',
                quality: result.quality || 'full',
                note: null,
            };

        } catch (err) {
            console.error('[documentIntelligence] PDF extraction failed:', err);
            return {
                text: '',
                pageCount: 0,
                wordCount: 0,
                method: 'failed',
                quality: 'failed',
                note: `Extraction error: ${err.message}`,
            };
        }
    },

    extractFromWord: async (file) => {
        const mammoth = await import('mammoth');
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        const text = result.value;
        const wordCount = text.split(/\s+/).filter(Boolean).length;
        return {
            text,
            pageCount: null, // Word docs don't have reliable page counts
            wordCount,
            method: 'word_text',
            quality: wordCount > 20 ? 'full' : 'partial',
        };
    },

    extractFromExcel: async (file) => {
        const XLSX = await import('xlsx');
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheets = workbook.SheetNames.map(name => {
            const sheet = workbook.Sheets[name];
            const csv = XLSX.utils.sheet_to_csv(sheet);
            return `[Sheet: ${name}]\n${csv}`;
        });
        const fullText = sheets.join('\n\n');
        const wordCount = fullText.split(/\s+/).filter(Boolean).length;
        return {
            text: fullText,
            pageCount: workbook.SheetNames.length,
            wordCount,
            method: 'excel_csv',
            quality: wordCount > 10 ? 'full' : 'partial',
        };
    },

    extractFromCSV: async (file) => {
        const text = await file.text();
        const wordCount = text.split(/\s+/).filter(Boolean).length;
        return {
            text,
            pageCount: 1,
            wordCount,
            method: 'csv_text',
            quality: 'full',
        };
    },
};
